import { useUserIpApi } from '~/api/use-user-ip-api.js';
import { getCookie, setCookie } from '~/utils/cookies.js';

const BASE_URL = 'https://direct.dy-api.com';
const COLLECT_BASE_URL = 'https://direct-collect.dy-api.com';

const TIMEOUT_MS = 7000;

const DYNAMIC_YIELD_COOKIES = {
  SERVER: '_dyid_server',
  SESSION: '_dyjsession',
  USER: '_dyid_server',
};

/**
 * Dynamic Yield API composable.
 * Provides methods for interacting with Dynamic Yield's API.
 *
 * @returns {import('~/types/dynamic-yield').DynamicYieldApiReturn} API methods.
 */
export function useDynamicYieldApi() {
  const { getUserIp } = useUserIpApi();

  /**
   * Checks if Dynamic Yield is properly initialized.
   *
   * @returns {boolean} Whether Dynamic Yield is initialized.
   */
  const isDynamicYieldInitialized = () => {
    return window.DY && window.DY.recommendationContext && window.DY.apiKey;
  };

  /**
   * Processes and sets cookies from a Dynamic Yield API response.
   *
   * @param {import('~/types/dynamic-yield').DynamicYieldResponse} response - The Dynamic Yield API response.
   */
  const processApiCookies = (response) => {
    if (!response || !response.cookies || !Array.isArray(response.cookies)) {
      return;
    }
    response.cookies.forEach((cookie) => {
      if (!cookie.name || cookie.value === undefined) return;

      const options = { ...cookie };
      if (typeof options.maxAge === 'string') {
        options.maxAge = parseInt(options.maxAge, 10);
      }
      setCookie(cookie.name, cookie.value, options);
    });
  };

  /**
   * Normalizes the selector input into an object with a names array and optional preview.
   *
   * @param {Object|string} selector - The selector object or name.
   * @returns {import('~/types/dynamic-yield').DynamicYieldSelector} Normalized selector object.
   */
  const normalizeSelector = (selector) => {
    let normalized = {};
    if (typeof selector === 'string') {
      normalized.names = [selector];
    } else if (typeof selector === 'object') {
      if (selector.name) {
        normalized = { ...selector, names: [selector.name] };
        delete normalized.name;
      } else if (selector.names) {
        normalized = { ...selector };
      } else {
        normalized = { ...selector, names: [] };
      }
    }
    const dyApiPreviewParam = new URLSearchParams(window.location.search).get('dyApiPreview');
    if (dyApiPreviewParam) {
      normalized.preview = { ids: [dyApiPreviewParam] };
    }
    return normalized;
  };

  /**
   * Helper function to send a POST request to the Dynamic Yield API.
   *
   * @param {string} url - The endpoint URL.
   * @param {Object} requestBody - The request body object.
   * @param {Object} headers - Additional headers.
   * @returns {Promise<import('~/types/dynamic-yield').DynamicYieldResponse>} The JSON response.
   */
  const dyFetch = async (url, requestBody, headers) => {
    const fetchOptions = {
      body: JSON.stringify(requestBody),
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
        'DY-API-KEY': window?.DY?.apiKey,
        ...headers,
      },
      method: 'POST',
    };

    // Create a timeout promise that rejects after TIMEOUT_MS milliseconds
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error(`DynamicYield API request timed out after ${TIMEOUT_MS}ms`));
      }, TIMEOUT_MS);
    });

    // Race the fetch against the timeout
    const response = await Promise.race([fetch(url, fetchOptions), timeoutPromise]);

    if (!response.ok) {
      throw new Error(`DynamicYield API error: ${response.status}`);
    }
    const data = await response.json();
    if (data.cookies && Array.isArray(data.cookies)) {
      processApiCookies(data);
    }
    return data;
  };

  /**
   * Makes a choose API call for Dynamic Yield recommendations.
   *
   * @param {Object|string} selector - The selector object or name.
   * @param {string} [userIp] - The user's IP address (optional).
   * @returns {Promise<import('~/types/dynamic-yield').DynamicYieldResponse>} A promise resolving to the API response.
   */
  const choose = async (selector, userIp) => {
    if (!isDynamicYieldInitialized()) {
      return Promise.reject(new Error('DynamicYield not properly initialized'));
    }

    const recommendationContext = { ...window.DY.recommendationContext };
    recommendationContext.data = recommendationContext.data ?? [];

    const normalizedSelector = normalizeSelector(selector);
    const ipAddress = userIp || (await getUserIp());
    const serverCookie = getCookie(DYNAMIC_YIELD_COOKIES.SERVER);
    const sessionCookie = getCookie(DYNAMIC_YIELD_COOKIES.SESSION);

    const requestBody = {
      context: {
        device: {
          ip: ipAddress || undefined,
          userAgent: window.navigator.userAgent,
        },
        page: {
          ...recommendationContext,
          location: window.location.href,
        },
      },
      options: {
        returnAnalyticsMetadata: true,
      },
      selector: normalizedSelector,
      session: {
        dy: sessionCookie || '',
      },
      user: {
        dyid: serverCookie,
        dyid_server: serverCookie,
      },
    };

    const url = `${BASE_URL}/v2/serve/user/choose`;
    return await dyFetch(url, requestBody);
  };

  /**
   * Reports click events to Dynamic Yield.
   *
   * @param {Array<import('~/types/dynamic-yield').DynamicYieldEngagement>} engagement - Engagement data.
   * @returns {Promise<Object>} A promise resolving to the API response.
   */
  const reportEngagement = async (engagement) => {
    if (!isDynamicYieldInitialized()) {
      return Promise.resolve({ success: false });
    }

    const serverCookie = getCookie(DYNAMIC_YIELD_COOKIES.SERVER);
    const sessionCookie = getCookie(DYNAMIC_YIELD_COOKIES.SESSION);

    const requestBody = {
      engagements: [...engagement],
      session: { dy: sessionCookie || '' },
      user: {
        dyid: serverCookie,
        dyid_server: serverCookie,
      },
    };

    const url = `${COLLECT_BASE_URL}/v2/collect/user/engagement`;
    try {
      return await dyFetch(url, requestBody, {
        Origin: window.location.origin,
        Referer: window.location.href,
        'User-Agent': window.navigator.userAgent,
      });
    } catch (error) {
      return { error: error.message, success: false };
    }
  };

  return {
    choose,
    isDynamicYieldInitialized,
    processApiCookies,
    reportEngagement,
  };
}
